import { FC, Fragment, MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import GroupIcon from "@mui/icons-material/Group";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Divider,
  Popover,
  Typography,
} from "@mui/material";
import { informationStyles as styles } from "./informationStyles";

type HoveredRole = "globalAdmin" | "multisiteManager";

export const renderWithRedAsterisks = (text: string) =>
  text.split("*").map((part, i) =>
    i % 2 === 1 ? (
      <Fragment key={i}>
        <span style={{ color: "red" }}>*</span>
        {part}
      </Fragment>
    ) : (
      part
    ),
  );

const RolePopoverContent: FC<{
  role: HoveredRole;
  t: (key: string) => string;
}> = ({ role, t }) => (
  <Box sx={styles.popover.container}>
    <Typography variant="overline" sx={styles.popover.rolesLabel}>
      {t("Users_managers.available_roles")}
    </Typography>
    <Divider sx={styles.popover.divider} />
    {role === "globalAdmin" ? (
      <>
        <Box sx={styles.popover.roleHeader}>
          <AdminPanelSettingsIcon color="primary" fontSize="small" />
          <Typography fontWeight={700}>Global Admin</Typography>
        </Box>
        <Typography variant="body2" sx={styles.popover.roleDesc}>
          {t("Users_managers.global_admin_desc")}
        </Typography>
        <Box sx={styles.popover.chipsBox}>
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon />}
            label={t("Users_managers.global_admin_all_sites")}
            color="success"
            variant="outlined"
          />
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon />}
            label={t("Users_managers.global_admin_all_groups")}
            color="success"
            variant="outlined"
          />
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon />}
            label={t("Users_managers.global_admin_global_config")}
            color="success"
            variant="outlined"
          />
        </Box>
      </>
    ) : (
      <>
        <Box sx={styles.popover.roleHeader}>
          <GroupIcon color="primary" fontSize="small" />
          <Typography fontWeight={700}>Multisite Manager</Typography>
        </Box>
        <Typography variant="body2" sx={styles.popover.multisiteDesc}>
          {renderWithRedAsterisks(t("Users_managers.multisite_manager_desc"))}
        </Typography>
      </>
    )}
  </Box>
);

const Information: FC = () => {
  const [t] = useTranslation("global");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredRole, setHoveredRole] = useState<HoveredRole | null>(null);

  const handleMouseEnter = (
    event: MouseEvent<HTMLDivElement>,
    role: HoveredRole,
  ) => {
    setAnchorEl(event.currentTarget);
    setHoveredRole(role);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
    setHoveredRole(null);
  };

  return (
    <>
      <Alert
        severity="info"
        icon={<AdminPanelSettingsIcon fontSize="inherit" />}
        sx={styles.alert.root}
      >
        <AlertTitle sx={styles.alert.title}>
          {t("Users_managers.info_title")}
        </AlertTitle>
        <Typography variant="body2">
          {renderWithRedAsterisks(t("Users_managers.info_sub"))}
        </Typography>
        <Box sx={styles.alert.chipsRow}>
          <span style={styles.alert.rolesLabel}>
            {t("Users_managers.available_roles")} :
          </span>
          <Chip
            label="Global Admin"
            icon={<AdminPanelSettingsIcon />}
            size="small"
            variant="outlined"
            onMouseEnter={(e) =>
              handleMouseEnter(e as MouseEvent<HTMLDivElement>, "globalAdmin")
            }
            onMouseLeave={handleMouseLeave}
            sx={styles.alert.chip}
          />
          <Chip
            label="Multisite Manager"
            icon={<GroupIcon />}
            size="small"
            variant="outlined"
            onMouseEnter={(e) =>
              handleMouseEnter(
                e as MouseEvent<HTMLDivElement>,
                "multisiteManager",
              )
            }
            onMouseLeave={handleMouseLeave}
            sx={styles.alert.chip}
          />
        </Box>
      </Alert>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={styles.popover.root}
        disableRestoreFocus
      >
        {hoveredRole && <RolePopoverContent role={hoveredRole} t={t} />}
      </Popover>
    </>
  );
};

export default Information;
